// Admin Orders Management — dedicated page for real-time order operations.
// Extracted out of the main Admin Dashboard so ops stays focused and uncluttered.
//
// Features:
//   • Live Kanban board (6 status columns) with drill-down cards
//   • Filters: search (code/customer), status, fulfillment (delivery/pickup), date, restaurant
//   • Toggle between Kanban and Table view for large volumes
//   • Advance order state, assign / reassign driver, mark pickup delivered
//   • Auto-refresh every 20s + WebSocket live updates
//
// URL: /admin/orders
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import api, { extractError } from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import OrderReassignModal from "@/components/OrderReassignModal";
import {
  RefreshCw, Bike, ChefHat, PackageCheck, Home, X, UserCheck, MapPin,
  Store, Radio, ArrowRightLeft, Search, Filter, LayoutGrid, List,
  Truck, Package, Calendar, ChevronDown, ChevronLeft, Eye,
} from "lucide-react";
import ReceiptPreviewModal from "@/components/ReceiptPreviewModal";

const COLUMNS = [
  { key: "pending",   title: "طلب جديد",       color: "bg-yellow-50 border-yellow-200",  ring: "ring-yellow-300",  next: "preparing", nextLabel: "بدء التحضير", icon: ChefHat },
  { key: "preparing", title: "قيد التحضير",    color: "bg-blue-50 border-blue-200",  ring: "ring-blue-300",  next: "ready",     nextLabel: "جاهز",         icon: PackageCheck },
  { key: "ready",     title: "جاهز للاستلام",  color: "bg-blue-50 border-blue-200",      ring: "ring-blue-300",    next: null,        nextLabel: "تعيين سائق",   icon: Bike },
  { key: "assigned",  title: "مع السائق",      color: "bg-purple-50 border-purple-200",  ring: "ring-purple-300",  next: null, icon: Bike },
  { key: "picked_up", title: "في الطريق",      color: "bg-primary-light border-blue-300", ring: "ring-blue-300", next: null, icon: MapPin },
  { key: "delivered", title: "تم التسليم",     color: "bg-green-50 border-green-200",    ring: "ring-green-300",   next: null, icon: Home },
];

const STATUS_LABEL = Object.fromEntries(COLUMNS.map((c) => [c.key, c.title]));
const STATUS_TONE = {
  pending: "bg-yellow-100 text-yellow-800",
  preparing: "bg-blue-100 text-blue-800",
  ready: "bg-blue-100 text-blue-800",
  assigned: "bg-purple-100 text-purple-800",
  picked_up: "bg-primary-light text-primary-brand",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("ar-LY", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("ar-LY", { day: "2-digit", month: "2-digit" }); }
  catch { return ""; }
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [reassignOrder, setReassignOrder] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [err, setErr] = useState("");

  // Filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | all | delivered | cancelled | <status>
  const [fulfillmentFilter, setFulfillmentFilter] = useState("all"); // all | delivery | pickup
  const [restaurantFilter, setRestaurantFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("today"); // today | 3d | 7d | all
  const [view, setView] = useState("kanban"); // kanban | table
  const [showFilters, setShowFilters] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [o, d] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/drivers"),
      ]);
      setOrders(o.data);
      setDrivers(d.data);
    } catch (e) { setErr(extractError(e)); }
  }, []);

  useEffect(() => {
    refresh();
    (async () => {
      try {
        const { data } = await api.get("/restaurants");
        setRestaurants(Array.isArray(data) ? data : (data?.restaurants || []));
      } catch { /* ignore */ }
    })();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  const { status: wsStatus } = useWebSocket({
    onMessage: (msg) => { if (msg.event?.startsWith("order.")) refresh(); },
  });

  const updateStatus = async (id, status) => {
    setBusy(true);
    try { await api.patch(`/admin/orders/${id}/status`, { status }); await refresh(); }
    catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const autoAssign = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/orders/${id}/assign`); await refresh(); setAssignFor(null); }
    catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const manualAssign = async (orderId, driverUserId) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${orderId}/assign?driver_user_id=${driverUserId}`);
      await refresh(); setAssignFor(null);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  // Apply filters
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const dayMs = 24 * 60 * 60 * 1000;
    const startOf3d = new Date(now - 3 * dayMs).toISOString();
    const startOf7d = new Date(now - 7 * dayMs).toISOString();

    return orders.filter((o) => {
      // Date filter
      const created = o.created_at || "";
      if (dateFilter === "today" && created < startOfToday) return false;
      if (dateFilter === "3d" && created < startOf3d) return false;
      if (dateFilter === "7d" && created < startOf7d) return false;

      // Status filter
      if (statusFilter === "active" && ["delivered", "cancelled"].includes(o.status)) return false;
      if (["pending", "preparing", "ready", "assigned", "picked_up", "delivered", "cancelled"].includes(statusFilter)
          && o.status !== statusFilter) return false;

      // Fulfillment
      if (fulfillmentFilter !== "all" && o.fulfillment !== fulfillmentFilter) return false;

      // Restaurant
      if (restaurantFilter && o.restaurant_id !== restaurantFilter && o.branch?.id !== restaurantFilter) return false;

      // Search
      if (ql) {
        const hay = `${o.code} ${o.customer_name || ""} ${o.customer_phone || ""}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [orders, q, statusFilter, fulfillmentFilter, restaurantFilter, dateFilter]);

  const byStatus = (k) => filtered.filter((o) => o.status === k);

  const stats = {
    total: filtered.length,
    active: filtered.filter((o) => !["delivered", "cancelled"].includes(o.status)).length,
    delivered: filtered.filter((o) => o.status === "delivered").length,
    revenue: filtered.filter((o) => o.status === "delivered").reduce((s, o) => s + o.total, 0),
  };

  const activeFiltersCount = [
    q, statusFilter !== "active" && statusFilter, fulfillmentFilter !== "all" && fulfillmentFilter,
    restaurantFilter, dateFilter !== "today" && dateFilter,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar/>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">

        {/* Breadcrumb + Header */}
        <div className="flex items-center gap-2 mb-4 text-sm">
          <Link to="/admin" className="flex items-center gap-1 text-primary-brand hover:underline font-bold" data-testid="admin-back">
            <ChevronLeft size={14}/> لوحة الإدارة
          </Link>
          <span className="text-[var(--text-secondary)]">/</span>
          <span className="font-black" style={{fontFamily:"Cairo"}}>إدارة الطلبات</span>
        </div>

        {/* Title bar */}
        <div className="bg-white rounded-2xl talabya-shadow-soft p-4 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-primary-brand text-white flex items-center justify-center">
                <PackageCheck size={22}/>
              </div>
              <div>
                <h1 className="font-black text-xl leading-none" style={{fontFamily:"Cairo"}}>إدارة الطلبات</h1>
                <div className="text-xs text-[var(--text-secondary)] mt-1">لوحة العمليات المباشرة لكل طلب في النظام</div>
              </div>
              {wsStatus === "open" && (
                <span className="flex items-center gap-1 text-[10px] text-green-700 bg-green-50 px-2 py-1 rounded-full font-bold" data-testid="orders-ws-live">
                  <Radio size={9} className="animate-pulse-dot"/> بث لحظي
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-[var(--surface-alt)] rounded-xl p-1">
                <button onClick={() => setView("kanban")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black flex items-center gap-1 ${view === "kanban" ? "bg-white text-primary-brand talabya-shadow-soft" : "text-[var(--text-secondary)]"}`}
                        data-testid="orders-view-kanban">
                  <LayoutGrid size={12}/> لوحات
                </button>
                <button onClick={() => setView("table")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black flex items-center gap-1 ${view === "table" ? "bg-white text-primary-brand talabya-shadow-soft" : "text-[var(--text-secondary)]"}`}
                        data-testid="orders-view-table">
                  <List size={12}/> جدول
                </button>
              </div>
              <button onClick={refresh}
                      className="px-3 py-2 rounded-xl bg-white border border-[var(--border)] hover:bg-[var(--surface-alt)] text-xs font-bold flex items-center gap-1"
                      data-testid="orders-refresh">
                <RefreshCw size={12}/> تحديث
              </button>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 pt-4 border-t border-[var(--border)]">
            <StatCard label="نتائج الفلترة" value={stats.total} icon={Package} tone="slate"/>
            <StatCard label="نشطة" value={stats.active} icon={ChefHat} tone="orange"/>
            <StatCard label="مسلّمة" value={stats.delivered} icon={Home} tone="emerald"/>
            <StatCard label="إيراد" value={`${stats.revenue.toFixed(2)} د.ل`} icon={Truck} tone="sky"/>
          </div>
        </div>

        {/* Filters bar */}
        <div className="bg-white rounded-2xl talabya-shadow-soft p-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-[var(--surface-alt)] rounded-xl px-3 py-2">
              <Search size={14} className="text-[var(--text-secondary)] shrink-0"/>
              <input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="بحث برقم الطلب، اسم الزبون، أو الهاتف..."
                     className="flex-1 bg-transparent outline-none text-sm min-w-0"
                     data-testid="orders-search"/>
              {q && (
                <button onClick={() => setQ("")} className="text-[var(--text-secondary)] hover:text-primary-brand">
                  <X size={14}/>
                </button>
              )}
            </div>

            {/* Filter toggle */}
            <button onClick={() => setShowFilters((s) => !s)}
                    className={`px-3 py-2 rounded-xl border text-xs font-bold flex items-center gap-1 ${activeFiltersCount || showFilters ? "border-primary-brand text-primary-brand bg-blue-50" : "border-[var(--border)] text-[var(--text-secondary)] bg-white"}`}
                    data-testid="orders-filter-toggle">
              <Filter size={12}/> الفلاتر
              {activeFiltersCount > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary-brand text-white text-[10px] font-black flex items-center justify-center">{activeFiltersCount}</span>
              )}
              <ChevronDown size={12} className={`transition-transform ${showFilters ? "rotate-180" : ""}`}/>
            </button>
          </div>

          {showFilters && (
            <div className="mt-3 pt-3 border-t border-[var(--border)] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <FilterSelect label="الحالة" value={statusFilter} onChange={setStatusFilter} testid="orders-filter-status"
                            options={[
                              { value: "active", label: "الطلبات النشطة فقط" },
                              { value: "all", label: "الكل (متضمن المُسلَّمة)" },
                              ...COLUMNS.map((c) => ({ value: c.key, label: c.title })),
                              { value: "cancelled", label: "الملغاة" },
                            ]}/>
              <FilterSelect label="طريقة الاستلام" value={fulfillmentFilter} onChange={setFulfillmentFilter} testid="orders-filter-fulfillment"
                            options={[
                              { value: "all", label: "الكل" },
                              { value: "delivery", label: "توصيل" },
                              { value: "pickup", label: "استلام من المحل" },
                            ]}/>
              <FilterSelect label="المطعم" value={restaurantFilter} onChange={setRestaurantFilter} testid="orders-filter-restaurant"
                            options={[{ value: "", label: "كل المطاعم" }, ...restaurants.map((r) => ({ value: r.id, label: r.name }))]}/>
              <FilterSelect label="الفترة" value={dateFilter} onChange={setDateFilter} testid="orders-filter-date"
                            options={[
                              { value: "today", label: "اليوم" },
                              { value: "3d", label: "آخر 3 أيام" },
                              { value: "7d", label: "آخر 7 أيام" },
                              { value: "all", label: "كل الفترات" },
                            ]}/>
            </div>
          )}
        </div>

        {err && <div className="bg-red-50 text-red-600 px-4 py-2 rounded-xl text-sm mb-3">{err}</div>}

        {/* Body */}
        {view === "kanban" ? (
          <KanbanView columns={COLUMNS} byStatus={byStatus} busy={busy}
                      updateStatus={updateStatus}
                      onAssignOpen={setAssignFor}
                      onReassignOpen={setReassignOrder}
                      onPreview={setPreviewOrder}/>
        ) : (
          <TableView orders={filtered} busy={busy} updateStatus={updateStatus}
                     onAssignOpen={setAssignFor} onReassignOpen={setReassignOrder}
                     onPreview={setPreviewOrder}/>
        )}
      </div>

      {/* Modals */}
      {assignFor && (
        <AssignDriverModal order={assignFor} drivers={drivers} busy={busy}
                           onClose={() => setAssignFor(null)}
                           onAuto={() => autoAssign(assignFor.id)}
                           onManual={(uid) => manualAssign(assignFor.id, uid)}/>
      )}
      {reassignOrder && <OrderReassignModal order={reassignOrder} onClose={() => setReassignOrder(null)} onDone={refresh}/>}
      {previewOrder && <ReceiptPreviewModal order={previewOrder} onClose={() => setPreviewOrder(null)}/>}
    </div>
  );
}

// ---------------- Sub-components ----------------

function StatCard({ label, value, icon: Icon, tone }) {
  const tones = {
    slate:   "bg-slate-50 border-slate-200 text-slate-700",
    orange:  "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    sky:     "bg-sky-50 border-sky-200 text-sky-700",
  }[tone] || "bg-white border-[var(--border)]";
  return (
    <div className={`rounded-xl border p-3 flex items-center gap-3 ${tones}`}>
      <div className="w-10 h-10 rounded-lg bg-white/60 flex items-center justify-center"><Icon size={18}/></div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold opacity-80">{label}</div>
        <div className="font-black text-lg leading-none mt-0.5" style={{fontFamily:"Cairo"}}>{value}</div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold text-[var(--text-secondary)] mb-1 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm font-bold focus:border-primary-brand focus:outline-none"
              data-testid={testid}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function KanbanView({ columns, byStatus, busy, updateStatus, onAssignOpen, onReassignOpen, onPreview }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {columns.map((col) => {
        const items = byStatus(col.key);
        const Icon = col.icon;
        return (
          <div key={col.key} className={`flex-shrink-0 w-72 rounded-2xl p-3 border-2 ${col.color}`} data-testid={`col-${col.key}`}>
            <div className="flex justify-between items-center pb-3 mb-3 border-b border-[var(--border)]">
              <div className="font-black flex items-center gap-1.5 text-sm" style={{fontFamily:"Cairo"}}>
                <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center"><Icon size={14}/></div>
                {col.title}
              </div>
              <div className="bg-white text-[var(--text-secondary)] text-xs font-black px-2.5 py-1 rounded-md talabya-shadow-soft" data-testid={`col-${col.key}-count`}>{items.length}</div>
            </div>
            <div className="flex flex-col gap-2 max-h-[calc(100vh-400px)] overflow-y-auto pr-1">
              {items.length === 0 && (
                <div className="text-center text-xs text-[var(--text-secondary)] py-6">لا توجد طلبات</div>
              )}
              {items.map((o) => (
                <OrderCard key={o.id} order={o} col={col} busy={busy}
                           updateStatus={updateStatus}
                           onAssignOpen={onAssignOpen}
                           onReassignOpen={onReassignOpen}
                           onPreview={onPreview}/>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OrderCard({ order: o, col, busy, updateStatus, onAssignOpen, onReassignOpen, onPreview }) {
  return (
    <div className="bg-white rounded-xl p-3 talabya-shadow-soft border border-[var(--border)] hover:border-primary-brand transition-colors" data-testid={`ord-${o.id}`}>
      <div className="flex justify-between items-center mb-1.5">
        <div className="text-[10px] font-black text-primary-brand bg-primary-light px-2 py-0.5 rounded" data-testid={`ord-${o.id}-code`}>{o.code}</div>
        <div className="text-[10px] text-[var(--text-secondary)]">{fmtTime(o.created_at)}</div>
      </div>
      <div className="text-xs font-black mb-0.5">{o.customer_name}</div>
      {o.branch && (
        <div className="text-[10px] text-primary-brand font-bold flex items-center gap-1 mb-1">
          <Store size={10}/> {o.branch.name}
        </div>
      )}
      <div className="text-[11px] text-[var(--text-secondary)] line-clamp-2 mb-2">
        {o.items.map((i) => `${i.name} ×${i.quantity}`).join("، ")}
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-[var(--border)]">
        <div className="text-[10px] text-[var(--text-secondary)] flex items-center gap-1">
          {o.fulfillment === "delivery" ? <><Truck size={10}/> توصيل</> : <><Package size={10}/> استلام</>}
        </div>
        <div className="font-black text-primary-brand text-xs" style={{fontFamily:"Cairo"}}>{o.total.toFixed(2)} د.ل</div>
      </div>

      {col.next && (
        <button onClick={() => updateStatus(o.id, col.next)} disabled={busy}
                className="w-full mt-2 py-1.5 rounded-lg bg-primary-brand text-white text-xs font-bold hover:opacity-90 disabled:opacity-60"
                data-testid={`advance-${o.id}`}>
          {col.nextLabel} →
        </button>
      )}
      {col.key === "ready" && o.fulfillment === "delivery" && !o.driver_id && (
        <button onClick={() => onAssignOpen(o)} disabled={busy}
                className="w-full mt-2 py-1.5 rounded-lg bg-[var(--secondary)] text-white text-xs font-bold hover:opacity-90"
                data-testid={`assign-open-${o.id}`}>
          <UserCheck size={12} className="inline ml-1"/> تعيين سائق
        </button>
      )}
      {col.key === "ready" && o.fulfillment === "pickup" && (
        <button onClick={() => updateStatus(o.id, "delivered")} disabled={busy}
                className="w-full mt-2 py-1.5 rounded-lg bg-green-600 text-white text-xs font-bold"
                data-testid={`pickup-done-${o.id}`}>
          تم الاستلام
        </button>
      )}
      {(o.driver_id || col.key === "ready") && o.fulfillment === "delivery" && !["delivered", "cancelled"].includes(o.status) && (
        <button onClick={() => onReassignOpen(o)} disabled={busy}
                className="w-full mt-1 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 text-[11px] font-black flex items-center justify-center gap-1"
                data-testid={`reassign-open-${o.id}`}
                title="تغيير السائق أو إلغاء الإسناد">
          <ArrowRightLeft size={11}/> {o.driver_id ? "تغيير السائق" : "إسناد سائق"}
        </button>
      )}
      <button onClick={() => onPreview(o)}
              className="w-full mt-1 py-1 rounded-lg bg-white border border-primary-brand/40 text-primary-brand text-[11px] font-black flex items-center justify-center gap-1 hover:bg-primary-light"
              data-testid={`preview-receipt-${o.id}`}>
        <Eye size={11}/> عرض الإيصال
      </button>
    </div>
  );
}

function TableView({ orders, busy, updateStatus, onAssignOpen, onReassignOpen, onPreview }) {
  if (!orders.length) {
    return (
      <div className="bg-white rounded-2xl p-10 text-center talabya-shadow-soft">
        <Package size={48} className="mx-auto text-[var(--text-secondary)] opacity-40 mb-3"/>
        <div className="font-black text-lg">لا توجد طلبات مطابقة</div>
        <div className="text-xs text-[var(--text-secondary)] mt-1">جرب تعديل الفلاتر أعلاه</div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl talabya-shadow-soft overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-alt)]">
            <tr>
              <th className="px-3 py-2 text-right font-black">الرقم</th>
              <th className="px-3 py-2 text-right font-black">الوقت</th>
              <th className="px-3 py-2 text-right font-black">الزبون</th>
              <th className="px-3 py-2 text-right font-black">المطعم</th>
              <th className="px-3 py-2 text-right font-black">الحالة</th>
              <th className="px-3 py-2 text-right font-black">النوع</th>
              <th className="px-3 py-2 text-right font-black">المبلغ</th>
              <th className="px-3 py-2 text-right font-black">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const col = COLUMNS.find((c) => c.key === o.status);
              return (
                <tr key={o.id} className="border-t border-[var(--border)] hover:bg-[var(--surface-alt)]/40" data-testid={`row-${o.id}`}>
                  <td className="px-3 py-2 font-black text-primary-brand">{o.code}</td>
                  <td className="px-3 py-2 text-xs">
                    <div>{fmtDate(o.created_at)}</div>
                    <div className="text-[var(--text-secondary)]">{fmtTime(o.created_at)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-bold">{o.customer_name}</div>
                    <div className="text-[10px] text-[var(--text-secondary)]" dir="ltr">{o.customer_phone || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{o.branch?.name || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black ${STATUS_TONE[o.status] || "bg-gray-100 text-gray-700"}`}>
                      {STATUS_LABEL[o.status] || o.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {o.fulfillment === "delivery" ? <span className="flex items-center gap-1"><Truck size={11}/> توصيل</span> : <span className="flex items-center gap-1"><Package size={11}/> استلام</span>}
                  </td>
                  <td className="px-3 py-2 font-black text-primary-brand">{o.total.toFixed(2)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {col?.next && (
                        <button onClick={() => updateStatus(o.id, col.next)} disabled={busy}
                                className="px-2 py-1 rounded bg-primary-brand text-white text-[10px] font-bold hover:opacity-90 disabled:opacity-60"
                                data-testid={`row-advance-${o.id}`}>
                          {col.nextLabel}
                        </button>
                      )}
                      {o.status === "ready" && o.fulfillment === "delivery" && !o.driver_id && (
                        <button onClick={() => onAssignOpen(o)} disabled={busy}
                                className="px-2 py-1 rounded bg-[var(--secondary)] text-white text-[10px] font-bold"
                                data-testid={`row-assign-${o.id}`}>
                          تعيين سائق
                        </button>
                      )}
                      {o.driver_id && !["delivered", "cancelled"].includes(o.status) && (
                        <button onClick={() => onReassignOpen(o)} disabled={busy}
                                className="px-2 py-1 rounded bg-amber-100 text-amber-800 text-[10px] font-bold hover:bg-amber-200"
                                data-testid={`row-reassign-${o.id}`}>
                          تغيير سائق
                        </button>
                      )}
                      <button onClick={() => onPreview(o)}
                              className="px-2 py-1 rounded bg-primary-light text-primary-brand text-[10px] font-bold hover:bg-primary-brand hover:text-white flex items-center gap-1"
                              data-testid={`row-preview-${o.id}`}>
                        <Eye size={10}/> عرض
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AssignDriverModal({ order, drivers, busy, onClose, onAuto, onManual }) {
  const available = drivers.filter((d) => d.status === "available");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="assign-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-[var(--border)]">
          <div>
            <div className="text-xs text-[var(--text-secondary)]">تعيين سائق للطلب</div>
            <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>{order.code}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center" data-testid="assign-close"><X size={16}/></button>
        </div>
        <div className="p-5">
          <button onClick={onAuto} disabled={busy}
                  className="w-full mb-4 py-3 rounded-xl bg-primary-brand text-white font-bold talabya-shadow-float disabled:opacity-60"
                  data-testid="assign-auto">
            🎯 تعيين تلقائي (الأقرب)
          </button>
          <div className="text-xs text-[var(--text-secondary)] mb-2 font-bold">أو اختر يدوياً</div>
          <div className="max-h-72 overflow-y-auto flex flex-col gap-2">
            {available.length === 0 ? (
              <div className="text-center text-sm text-[var(--text-secondary)] py-4">لا يوجد سائقون متاحون حالياً</div>
            ) : available.map((d) => (
              <div key={d.id} className="flex items-center gap-3 p-3 bg-[var(--surface-alt)] rounded-xl" data-testid={`drv-row-${d.user_id}`}>
                <div className="w-10 h-10 rounded-full bg-primary-brand text-white flex items-center justify-center font-black" style={{fontFamily:"Cairo"}}>{d.name?.[0]}</div>
                <div className="flex-1">
                  <div className="font-bold text-sm">{d.name}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{d.vehicle} · ⭐ {d.rating}</div>
                </div>
                <button onClick={() => onManual(d.user_id)} disabled={busy}
                        className="px-3 py-1.5 rounded-lg bg-primary-brand text-white text-xs font-bold disabled:opacity-60"
                        data-testid={`assign-${d.user_id}`}>
                  تعيين
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
